import React, { useEffect, useRef } from 'react'
import { CircularProgress, Grid, Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Paper } from '@e3dc-react/shell/Elements/Paper'
import useResizeObserver from 'use-resize-observer'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import PieCharts from '../../default-charts/components/PieCharts'
import { PocPerformantChart } from '../../default-charts/components'
import {
  ChartSettings,
  CustomLayout,
  FetchingState,
  getChartsEditingSettings,
  SaveChartProps,
  useChartDateBarContext,
  useLastTimeFetched,
} from 'src/Libs/chart-kit'
import { useIsWebviewMode, useUserSettings } from 'src/Hooks'
import { DASHBOARD_POC_SYSTEM_CHARTS_ID } from 'src/Libs/qa/ids'
import { useIsElementVisible } from 'src/Libs/navigation'
import { useSystemChartsContext } from '../providers/SystemChartsProvider'
import ChartLoadingBackdrop from 'src/Elements/ChartLoadingBackdrop'
import CreateCustomChartButton from '../../custom-charts/components/CreateCustomChartButton'
import { DateSettings } from 'src/Libs/system-data-handler/types'
import CustomCharts from '../../custom-charts/components/CustomCharts'
import { usePocData } from '../hooks/use-poc-data'
import useCustomCharts from '../hooks/use-custom-charts'
import useMappedCharts from '../hooks/use-mapped-charts'
import useSystemCharts from '../hooks/use-system-charts'
import { PerformanceAndConsumptionChartsProvider } from '../../default-charts/providers/PerformanceAndConsumptionChartsContext'
import PerformanceAndConsumptionCharts from '../../default-charts/components/PerformanceAndConsumptionCharts'

interface StyleProps {
  trigger: boolean
  customRange: boolean
  isWebView: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: '100%',
    maxWidth: '100%',
    marginTop: theme.spacing(3),
  },
  chartsContainer: {
    marginTop: theme.spacing(2),
    width: '100%',
    userSelect: 'none',
    '& .react-grid-item > .react-resizable-handle.react-resizable-handle-se': {
      zIndex: 9999,
      color: theme.palette.secondary.main,
    },
    [theme.breakpoints.up('sm')]: {
      userSelect: 'auto',
    },
  },
  gridContainer: {
    zIndex: 5,
    position: 'relative',
  },
  paperPerformanceValues: {
    display: 'flex',
    '& .MuiSpeedDial-root': {
      bottom: props.isWebView ? '72px' : '12px',
    },
  },
}))

interface Props {
  storageId?: string
  installationId?: number
  hide: boolean
  chartSettingsList: ChartSettings[]
  showSavingsPieChart?: boolean
  isSavingsChartLoading?: boolean
  defaultAndCustomCharts: CustomLayout<never>[]
  saveCustomChart: (props: SaveChartProps) => Promise<void>
}

/**
 * This component is proof of concept for system charts using performant API and will replace the existing SystemCharts component in the future
 */
const PocSystemCharts: React.FC<Props> = ({
  storageId,
  installationId,
  chartSettingsList,
  hide,
  showSavingsPieChart,
  isSavingsChartLoading,
  defaultAndCustomCharts,
  saveCustomChart,
}) => {
  const isWebView = useIsWebviewMode()
  const { t } = useCustomTranslation()
  const { ref: resizeRef } = useResizeObserver<HTMLDivElement>()
  const { isVisible, ref: visibilityRef } = useIsElementVisible()
  const { from, to, view, resolution, hasCustomRange, isForecastEnabled, setFrom, setTo, setView, setAreSystemChartsVisible } =
    useChartDateBarContext()
  const dateSettings: DateSettings = {
    from,
    to,
    view,
    isForecastEnabled,
    setFrom,
    setTo,
    setView,
  }
  const { classes } = useStyles({ trigger: false, customRange: hasCustomRange, isWebView })
  const chartContainerRef = useRef(null)
  const { resetChartSelection } = useSystemChartsContext()
  const data = usePocData(from, to, storageId ?? '', resolution ?? view)

  const historyDataFetchDate = useLastTimeFetched()

  const { customMappedCharts, handleSaveCustomChart, handleSaveDefaultCharts } = useCustomCharts(
    defaultAndCustomCharts,
    dateSettings,
    data,
    chartSettingsList,
    saveCustomChart,
    data.loadingState,
    installationId,
    storageId
  )

  const { userSettings } = useUserSettings()
  const chartSettings = getChartsEditingSettings(userSettings)
  const { handleSaveCharts } = useSystemCharts({ installationId: installationId ?? 0, storageId })
  const { directConsumptionChart, directConsumptionProductionChart } = useMappedCharts({
    handleSaveCharts,
    defaultAndCustomCharts,
    installationId,
    storageId,
    chartSettings,
    dateSettings,
    rechartData: data,
  })

  useEffect(() => {
    setAreSystemChartsVisible(isVisible && !hide)
  }, [isVisible, setAreSystemChartsVisible, hide])

  if (hide) return null

  if (from && to) {
    return (
      <Grid item xs={12} className={classes.gridContainer} onClick={resetChartSelection} id="dashboard-statistics">
        <Paper
          paperRef={chartContainerRef}
          title={t('dashboard.liveView.poc-performance-values')}
          subTitle={historyDataFetchDate}
          className={classes.paperPerformanceValues}
          dataQa={DASHBOARD_POC_SYSTEM_CHARTS_ID}
          headerChildren={<CreateCustomChartButton onClick={handleSaveCustomChart} />}
        >
          <Box ref={visibilityRef}>
            <Box className={classes.chartsContainer} ref={resizeRef}>
              <Grid container spacing={2} pb={4}>
                <Grid item xs={12}>
                  <PieCharts
                    data={data}
                    from={from}
                    to={to}
                    showForecast={isForecastEnabled}
                    view={view}
                    resolution={resolution}
                    handleSaveChart={handleSaveDefaultCharts}
                    showSavingsChart={showSavingsPieChart}
                    isSavingsChartLoading={isSavingsChartLoading}
                    isHistoryChartLoading={data.loadingState.state === FetchingState.IsFetching}
                  />
                </Grid>

                <Grid item xs={12}>
                  {directConsumptionChart && directConsumptionProductionChart && (
                    <PerformanceAndConsumptionChartsProvider>
                      <PerformanceAndConsumptionCharts
                        performanceChart={
                          <PocPerformantChart
                            data={data}
                            from={from}
                            to={to}
                            showForecast={isForecastEnabled}
                            view={view}
                            resolution={resolution}
                            handleSaveChart={handleSaveDefaultCharts}
                          />
                        }
                        directConsumptionCharts={[directConsumptionChart, directConsumptionProductionChart]}
                      />
                    </PerformanceAndConsumptionChartsProvider>
                  )}
                </Grid>
              </Grid>
              {!!customMappedCharts.length && <CustomCharts customCharts={customMappedCharts} />}
            </Box>
          </Box>
        </Paper>
        {data.loadingState.state === FetchingState.IsFetching ? <ChartLoadingBackdrop loadingState={data.loadingState} /> : null}
      </Grid>
    )
  }

  return (
    <Paper className={classes.center} elevation={1}>
      <CircularProgress />
    </Paper>
  )
}

export default PocSystemCharts
